import { Minus, Plus, ShoppingCart } from 'lucide-react';
import { DynamicIcon } from 'lucide-react/dynamic';
import React, { useMemo, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import api from '../../service/api';
import useShopItemsStore from '../../store/shopItems';
import useUserStore from '../../store/user';

interface ShopItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  itemId: string;
}

const ShopItemModal: React.FC<ShopItemModalProps> = ({
  isOpen,
  onClose,
  itemId,
}) => {
  const [quantity, setQuantity] = useState(1);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const { t } = useTranslation();

  const getShopItemById = useShopItemsStore(state => state.getShopItemById);
  const fetchShopItems = useShopItemsStore(state => state.fetchShopItems);
  const balance = useUserStore(state => state.balance);
  const updateBalance = useUserStore(state => state.updateBalance);

  const item = getShopItemById(itemId);

  const { totalPrice, isMinusEnabled, isPlusEnabled, canAfford } =
    useMemo(() => {
      if (!item)
        return {
          totalPrice: 0,
          isMinusEnabled: false,
          isPlusEnabled: false,
          canAfford: false,
        };

      const unit = item.effectivePrice ?? item.price;
      const total = unit * quantity;
      return {
        totalPrice: total,
        isMinusEnabled: quantity > 1,
        isPlusEnabled: total + unit <= balance,
        canAfford: total <= balance,
      };
    }, [item, quantity, balance]);

  if (!item || !isOpen) return null;

  const { title, description, icon, price, effectivePrice } = item;
  const hasDiscount = effectivePrice != null && effectivePrice !== price;

  const handleQuantityChange = (delta: number) => {
    setQuantity(prev => Math.max(1, prev + delta));
  };

  const handlePurchase = async () => {
    setIsPurchasing(true);

    try {
      await api.shopItemPurchase(itemId, quantity);
      await Promise.all([fetchShopItems(), updateBalance()]);
      onClose();
    } catch (error) {
      console.error('Failed to purchase item:', error);
    } finally {
      setIsPurchasing(false);
    }
  };

  const handleClose = () => {
    if (!isPurchasing) {
      setQuantity(1);
      onClose();
    }
  };

  return (
    <Modal
      show={isOpen}
      onHide={handleClose}
      centered
      size="lg"
      className="fs-3"
      contentClassName="position-relative"
      backdrop="static"
    >
      <Modal.Dialog
        className="m-0"
        style={{
          backgroundColor: 'rgba(6, 56, 96, 0.95)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
        }}
      >
        <div
          className="position-absolute top-0 start-50 translate-middle d-flex align-items-center justify-content-center rounded-circle bg-primary"
          style={{
            width: '64px',
            height: '64px',
            border: '4px solid rgba(6, 56, 96, 0.95)',
            zIndex: 1,
          }}
        >
          <DynamicIcon
            name={icon}
            size={32}
            strokeWidth={1.5}
            className="text-white"
          />
        </div>

        <Modal.Header
          closeButton={!isPurchasing}
          closeVariant="white"
          className="border-0 pt-5 pb-3"
          style={{ backgroundColor: 'transparent' }}
        >
          <Modal.Title className="fs-1 text-white fw-bold w-100 text-center">
            {title}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body
          className="text-white px-4 pb-4"
          style={{ backgroundColor: 'transparent' }}
        >
          {description && (
            <div className="mb-2">
              <h6 className="text-white-50 mb-2">{t('common.description')}</h6>
              <p className="mb-0">{description}</p>
            </div>
          )}

          <div className="mb-5">
            <h6 className="text-white-50 mb-4">{t('shop.quantity')}</h6>
            <div className="d-flex align-items-center justify-content-center gap-3">
              <Button
                variant="outline-light"
                size="lg"
                className="rounded-circle"
                style={{ width: '64px', height: '64px' }}
                onClick={() => handleQuantityChange(-1)}
                disabled={!isMinusEnabled || isPurchasing}
              >
                <Minus size={24} />
              </Button>

              <div
                className="fs-1 fw-bold text-center"
                style={{ minWidth: '80px' }}
              >
                {quantity}
              </div>

              <Button
                variant="outline-light"
                size="lg"
                className="rounded-circle"
                style={{ width: '64px', height: '64px' }}
                onClick={() => handleQuantityChange(1)}
                disabled={!isPlusEnabled || isPurchasing}
              >
                <Plus size={24} />
              </Button>
            </div>
          </div>

          <div className="mb-4 text-center">
            <h6 className="text-white-50 mb-2">{t('shop.totalCost')}</h6>
            {hasDiscount && (
              <div className="text-decoration-line-through text-white-50 fs-4">
                {price * quantity}{' '}
                {t('common.stickers', { count: price * quantity })}
              </div>
            )}
            <div className={`fs-1 fw-bold ${!canAfford ? 'text-danger' : ''}`}>
              {totalPrice} {t('common.stickers', { count: totalPrice })}
            </div>
            {!canAfford && (
              <small className="text-danger">
                {t('shop.insufficientFunds', { balance })}
              </small>
            )}
          </div>

          <div className="text-center">
            <Button
              variant="success"
              size="lg"
              className="px-5 py-3 fs-3 fw-bold"
              onClick={handlePurchase}
              disabled={isPurchasing || !canAfford}
              style={{ minWidth: '200px' }}
            >
              {isPurchasing ? (
                <>
                  <Spinner
                    animation="border"
                    size="sm"
                    className="me-2"
                    role="status"
                  />
                  {t('shop.purchasing')}
                </>
              ) : (
                <>
                  <ShoppingCart size={28} className="me-2">
                    {t('shop.buy')}
                  </ShoppingCart>
                </>
              )}
            </Button>
          </div>
        </Modal.Body>
      </Modal.Dialog>
    </Modal>
  );
};

export default ShopItemModal;
export type { ShopItemModalProps };
